import { useEffect, useState, useCallback } from 'react';
import { MediaPicker } from '@/components/feature/MediaPicker';
import {
  listAdminReviews, createAdminReview, updateAdminReviewStatus, deleteAdminReview,
  type AdminReviewInput,
} from '@/services/reviews';
import { listAdminProducts } from '@/services/products';
import type { Review, ReviewStatus, Product } from '@/domain/types';

// ── Helpers ───────────────────────────────────────────────────────────────────

function statusBadge(status: ReviewStatus) {
  const map: Record<ReviewStatus, string> = {
    approved: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
    pending:  'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
    rejected: 'bg-red-50 text-red-600 ring-1 ring-red-200',
  };
  return map[status] ?? 'bg-surface-100 text-surface-500';
}

function Stars({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'lg' }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <i key={i} className={`ri-star-fill ${size === 'lg' ? 'text-base' : 'text-xs'} ${i < rating ? 'text-amber-400' : 'text-surface-200'}`} />
      ))}
    </div>
  );
}

// ── Drawer ────────────────────────────────────────────────────────────────────

const DEFAULT_FORM: AdminReviewInput = {
  productId:    '',
  rating:       5,
  title:        '',
  content:      '',
  reviewerName: '',
  reviewerRole: '',
  reviewerAvatar: '',
  status:       'approved',
};

interface DrawerProps {
  products: Product[];
  onClose: () => void;
  onSaved: () => void;
}

function ReviewDrawer({ products, onClose, onSaved }: DrawerProps) {
  const [form, setForm]         = useState<AdminReviewInput>(DEFAULT_FORM);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [showPicker, setShowPicker] = useState(false);

  const set = <K extends keyof AdminReviewInput>(key: K, val: AdminReviewInput[K]) =>
    setForm((f) => ({ ...f, [key]: val }));

  const validate = () => {
    if (!form.productId)      return 'Please select a product.';
    if (!form.reviewerName.trim()) return 'Reviewer name is required.';
    if (!form.content.trim()) return 'Review content is required.';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setSaving(true);
    try {
      await createAdminReview(form);
      onSaved();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save review.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200 shrink-0">
          <div>
            <h2 className="text-base font-bold text-surface-900">Add Manual Review</h2>
            <p className="text-xs text-surface-500 mt-0.5">Create a verified review on behalf of a customer</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-500 transition-colors">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {error && (
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              <i className="ri-error-warning-line text-base mt-0.5 shrink-0" /> {error}
            </div>
          )}

          {/* Product */}
          <div>
            <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wide mb-1.5">
              Product <span className="text-red-500">*</span>
            </label>
            <select value={form.productId} onChange={(e) => set('productId', e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 bg-white" required>
              <option value="">Select a product</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          {/* Reviewer info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wide mb-1.5">
                Reviewer Name <span className="text-red-500">*</span>
              </label>
              <input type="text" value={form.reviewerName} onChange={(e) => set('reviewerName', e.target.value)} required
                placeholder="e.g. Sarah Miller"
                className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wide mb-1.5">Role / Company</label>
              <input type="text" value={form.reviewerRole} onChange={(e) => set('reviewerRole', e.target.value)}
                placeholder="e.g. WordPress Developer"
                className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wide mb-1.5">Avatar</label>
            <div className="flex items-center gap-3">
              {form.reviewerAvatar ? (
                <img src={form.reviewerAvatar} alt="avatar" className="w-10 h-10 rounded-full object-cover border border-surface-200 shrink-0" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              ) : (
                <div className="w-10 h-10 rounded-full bg-surface-100 border border-surface-200 flex items-center justify-center shrink-0">
                  <i className="ri-user-line text-surface-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <input type="url" value={form.reviewerAvatar} onChange={(e) => set('reviewerAvatar', e.target.value)}
                  placeholder="https://… or pick from media"
                  className="w-full px-3 py-2 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" />
              </div>
              <button type="button" onClick={() => setShowPicker(true)}
                className="shrink-0 px-3 py-2 rounded-lg border border-brand-300 bg-brand-50 hover:bg-brand-100 text-brand-600 text-xs font-semibold transition-colors flex items-center gap-1.5">
                <i className="ri-image-line" /> Pick
              </button>
            </div>
          </div>

          {/* Star rating */}
          <div>
            <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wide mb-2">Rating</label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button key={star} type="button" onClick={() => set('rating', star)} className="text-2xl leading-none transition-transform hover:scale-110">
                  <i className={`ri-star-fill ${star <= form.rating ? 'text-amber-400' : 'text-surface-200'}`} />
                </button>
              ))}
              <span className="ml-2 text-sm font-semibold text-surface-700">{form.rating}/5</span>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wide mb-1.5">Review Title</label>
            <input type="text" value={form.title} onChange={(e) => set('title', e.target.value)}
              placeholder="e.g. Excellent plugin, saves hours!"
              className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" />
          </div>

          {/* Content */}
          <div>
            <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wide mb-1.5">
              Review Content <span className="text-red-500">*</span>
            </label>
            <textarea value={form.content} onChange={(e) => set('content', e.target.value)} required rows={5}
              placeholder="Write the review content…"
              className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 resize-none" />
          </div>

          {/* Status */}
          <div>
            <label className="block text-xs font-semibold text-surface-500 uppercase tracking-wide mb-1.5">Status</label>
            <div className="grid grid-cols-3 gap-2">
              {(['approved', 'pending', 'rejected'] as ReviewStatus[]).map((s) => (
                <button key={s} type="button" onClick={() => set('status', s)}
                  className={`py-2 rounded-lg text-xs font-semibold capitalize border transition-colors ${
                    form.status === s ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-surface-200 text-surface-600 hover:border-surface-300'
                  }`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-surface-200 flex gap-3 shrink-0 bg-surface-50">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 rounded-lg border border-surface-300 hover:border-surface-400 text-surface-700 text-sm font-medium transition-colors">
            Cancel
          </button>
          <button type="button" onClick={handleSubmit as unknown as React.MouseEventHandler} disabled={saving}
            className="flex-1 px-4 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:bg-surface-300 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2">
            {saving && <i className="ri-loader-4-line animate-spin" />}
            {saving ? 'Saving…' : 'Add Review'}
          </button>
        </div>
      </div>

      {showPicker && (
        <MediaPicker
          selected={form.reviewerAvatar}
          title="Choose Reviewer Avatar"
          onSelect={(url) => set('reviewerAvatar', url)}
          onClose={() => setShowPicker(false)}
        />
      )}
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminReviews() {
  const [reviews, setReviews]     = useState<Review[]>([]);
  const [products, setProducts]   = useState<Product[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [search, setSearch]       = useState('');
  const [filterStatus, setFilterStatus] = useState<ReviewStatus | 'all'>('all');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [busyId, setBusyId]       = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const [revs, prods] = await Promise.all([listAdminReviews(), listAdminProducts()]);
      setReviews(revs);
      setProducts(prods);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load reviews.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (id: string, status: ReviewStatus) => {
    setBusyId(id);
    try {
      await updateAdminReviewStatus(id, status);
      setReviews((prev) => prev.map((r) => r.id === id ? { ...r, status } : r));
    } catch { /* silent */ } finally {
      setBusyId('');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this review? This cannot be undone.')) return;
    setBusyId(id);
    try {
      await deleteAdminReview(id);
      setReviews((prev) => prev.filter((r) => r.id !== id));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to delete review.');
    } finally {
      setBusyId('');
    }
  };

  const visible = reviews.filter((r) => {
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    const q = search.toLowerCase();
    return !q || r.reviewerName.toLowerCase().includes(q) || (r.productName ?? '').toLowerCase().includes(q) || r.content.toLowerCase().includes(q);
  });

  const counts = {
    all:      reviews.length,
    pending:  reviews.filter((r) => r.status === 'pending').length,
    approved: reviews.filter((r) => r.status === 'approved').length,
    rejected: reviews.filter((r) => r.status === 'rejected').length,
  };

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-surface-900">Reviews</h1>
          <p className="text-sm text-surface-500 mt-1">Manage customer reviews and add manual reviews.</p>
        </div>
        <button onClick={() => setDrawerOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors shrink-0 shadow-sm">
          <i className="ri-add-line" /> Add Manual Review
        </button>
      </div>

      {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      {/* Stats */}
      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {([['all', 'Total', 'ri-star-line', 'text-brand-600 bg-brand-50'], ['pending', 'Pending', 'ri-time-line', 'text-amber-600 bg-amber-50'], ['approved', 'Approved', 'ri-check-line', 'text-emerald-600 bg-emerald-50'], ['rejected', 'Rejected', 'ri-close-line', 'text-red-600 bg-red-50']] as [string, string, string, string][]).map(([key, label, icon, color]) => (
            <div key={key} className="bg-white rounded-xl border border-surface-200 p-4 cursor-pointer hover:border-brand-300 transition-colors" onClick={() => setFilterStatus(key as ReviewStatus | 'all')}>
              <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center mb-2`}>
                <i className={`${icon} text-sm`} />
              </div>
              <p className="text-xl font-bold text-surface-900">{counts[key as keyof typeof counts]}</p>
              <p className="text-xs text-surface-500">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-surface-400 text-sm" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by reviewer, product, or content…"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 bg-white" />
        </div>
        <div className="flex items-center gap-2">
          {(['all', 'pending', 'approved', 'rejected'] as (ReviewStatus | 'all')[]).map((s) => (
            <button key={s} onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${filterStatus === s ? 'bg-brand-600 text-white' : 'bg-white border border-surface-200 text-surface-600 hover:bg-surface-50'}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-surface-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead>
              <tr className="border-b border-surface-200 bg-surface-50/60">
                <th className="text-left px-5 py-3 text-xs font-semibold text-surface-500 uppercase tracking-wide">Reviewer</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-surface-500 uppercase tracking-wide">Product</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-surface-500 uppercase tracking-wide">Rating</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-surface-500 uppercase tracking-wide">Review</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-surface-500 uppercase tracking-wide">Status</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-surface-500 uppercase tracking-wide">Date</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-surface-400 text-sm">
                  <i className="ri-loader-4-line animate-spin text-2xl block mb-2" /> Loading reviews…
                </td></tr>
              )}
              {!loading && visible.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-surface-400 text-sm">
                  <i className="ri-star-line text-3xl block mb-2 text-surface-200" />
                  {reviews.length === 0 ? 'No reviews yet.' : 'No reviews match your filters.'}
                </td></tr>
              )}
              {visible.map((review) => (
                <tr key={review.id} className="border-b border-surface-50 hover:bg-surface-50/50 transition-colors group">

                  {/* Reviewer */}
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      {review.reviewerAvatar ? (
                        <img src={review.reviewerAvatar} alt={review.reviewerName} className="w-7 h-7 rounded-full object-cover shrink-0" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-brand-100 flex items-center justify-center text-brand-600 font-semibold text-xs shrink-0">
                          {review.reviewerName ? review.reviewerName[0].toUpperCase() : '?'}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-surface-900 truncate">{review.reviewerName || 'Anonymous'}</p>
                        {review.reviewerRole && <p className="text-xs text-surface-400 truncate">{review.reviewerRole}</p>}
                      </div>
                    </div>
                    {review.isManual && (
                      <span className="mt-1 inline-flex items-center gap-1 text-[10px] bg-violet-50 text-violet-600 font-semibold px-1.5 py-0.5 rounded">
                        <i className="ri-admin-line" /> Manual
                      </span>
                    )}
                  </td>

                  {/* Product */}
                  <td className="px-5 py-3">
                    <span className="text-sm text-surface-700">{review.productName ?? '—'}</span>
                  </td>

                  {/* Rating */}
                  <td className="px-5 py-3">
                    <Stars rating={review.rating} />
                  </td>

                  {/* Review content */}
                  <td className="px-5 py-3 max-w-xs">
                    {review.title && <p className="text-xs font-semibold text-surface-800 mb-0.5 truncate">{review.title}</p>}
                    <p className="text-xs text-surface-500 line-clamp-2">{review.content}</p>
                  </td>

                  {/* Status */}
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${statusBadge(review.status)}`}>
                      {review.status}
                    </span>
                  </td>

                  {/* Date */}
                  <td className="px-5 py-3 text-xs text-surface-400 whitespace-nowrap">
                    {new Date(review.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                  </td>

                  {/* Actions */}
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {review.status !== 'approved' && (
                        <button onClick={() => handleStatusChange(review.id, 'approved')} disabled={busyId === review.id} title="Approve"
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-emerald-50 text-surface-400 hover:text-emerald-600 transition-colors disabled:opacity-40">
                          <i className={busyId === review.id ? 'ri-loader-4-line animate-spin' : 'ri-check-line'} />
                        </button>
                      )}
                      {review.status !== 'rejected' && (
                        <button onClick={() => handleStatusChange(review.id, 'rejected')} disabled={busyId === review.id} title="Reject"
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-amber-50 text-surface-400 hover:text-amber-600 transition-colors disabled:opacity-40">
                          <i className="ri-close-line" />
                        </button>
                      )}
                      <button onClick={() => handleDelete(review.id)} disabled={busyId === review.id} title="Delete"
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-surface-400 hover:text-red-500 transition-colors disabled:opacity-40">
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drawer */}
      {drawerOpen && (
        <ReviewDrawer
          products={products}
          onClose={() => setDrawerOpen(false)}
          onSaved={() => { setDrawerOpen(false); load(); }}
        />
      )}
    </div>
  );
}
